import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ─── Login Screen ──────────────────────────────────────── */

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      if (res.data?.success) {
        onLogin(res.data.admin_key, res.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-screen" style={{
      minHeight: "100vh", background: "#070b14", display: "flex",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{ width: 400, padding: 40 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, marginBottom: 12 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ width: 4, borderRadius: 2, height: [14, 22, 30, 22, 14][i], background: "#10b981" }} />
            ))}
          </div>
          <h1 style={{ color: "#f1f5f9", fontSize: 22, fontWeight: 700, letterSpacing: 1, margin: "0 0 4px" }}>HIBOTICS AI</h1>
          <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>Middleware Control Center</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", color: "#94a3b8", fontSize: 12, marginBottom: 6, letterSpacing: 0.5 }}>Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: "100%", padding: "12px 16px", background: "#111827",
                border: "1px solid #1e293b", borderRadius: 8, color: "#f1f5f9",
                fontSize: 14, outline: "none", boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = "#10b981"}
              onBlur={e => e.target.style.borderColor = "#1e293b"}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", color: "#94a3b8", fontSize: 12, marginBottom: 6, letterSpacing: 0.5 }}>Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: "100%", padding: "12px 16px", background: "#111827",
                border: "1px solid #1e293b", borderRadius: 8, color: "#f1f5f9",
                fontSize: 14, outline: "none", boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = "#10b981"}
              onBlur={e => e.target.style.borderColor = "#1e293b"}
            />
          </div>
          {error && (
            <div data-testid="login-error" style={{
              background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8,
              padding: "10px 16px", marginBottom: 16, color: "#fca5a5", fontSize: 13,
            }}>{error}</div>
          )}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            style={{
              width: "100%", padding: "12px", background: loading ? "#064e3b" : "#10b981",
              border: "none", borderRadius: 8, color: "#fff", fontSize: 14,
              fontWeight: 600, cursor: loading ? "wait" : "pointer",
              transition: "background 0.2s",
            }}
          >{loading ? "Signing in..." : "Sign In"}</button>
        </form>
      </div>
    </div>
  );
}

/* ─── Shared Components ─────────────────────────────────── */

function StatusDot({ ok }) {
  return (
    <span data-testid="status-dot" style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: ok ? "#10b981" : "#ef4444",
      boxShadow: ok ? "0 0 8px #10b98166" : "0 0 8px #ef444466",
      marginRight: 8,
    }} />
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} style={{
      background: "linear-gradient(135deg, #111827 0%, #0d1424 100%)",
      border: "1px solid #1e293b", borderRadius: 12, padding: "24px 28px",
      flex: 1, minWidth: 140,
    }}>
      <div style={{ color: "#64748b", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ color: "#f1f5f9", fontSize: 28, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ color: "#10b981", fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Nav({ health, loading, view, onBack, onLogout }) {
  return (
    <nav data-testid="nav" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 40px", borderBottom: "1px solid #1e293b",
      background: "#0a0f1a", position: "sticky", top: 0, zIndex: 50,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 3, cursor: view !== "home" ? "pointer" : "default" }}
          onClick={view !== "home" ? onBack : undefined}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ width: 3, borderRadius: 2, height: [12, 18, 24, 18, 12][i], background: "#10b981" }} />
          ))}
        </div>
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 1, cursor: view !== "home" ? "pointer" : "default" }}
          onClick={view !== "home" ? onBack : undefined}>
          HIBOTICS AI
        </span>
        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "#1e293b", color: "#64748b" }}>MIDDLEWARE</span>
        {view !== "home" && (
          <button data-testid="back-btn" onClick={onBack} style={{
            background: "none", border: "1px solid #1e293b", color: "#94a3b8", borderRadius: 6,
            padding: "4px 14px", fontSize: 12, cursor: "pointer", marginLeft: 8,
          }}>All Clients</button>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StatusDot ok={health?.status === "healthy"} />
          <span style={{ fontSize: 13, color: health?.status === "healthy" ? "#10b981" : "#ef4444" }}>
            {health?.status === "healthy" ? "All systems operational" : loading ? "Connecting..." : "System error"}
          </span>
        </div>
        <button data-testid="logout-btn" onClick={onLogout} style={{
          background: "none", border: "1px solid #1e293b", color: "#64748b", borderRadius: 6,
          padding: "4px 14px", fontSize: 11, cursor: "pointer",
        }}>Logout</button>
      </div>
    </nav>
  );
}

const EVENT_COLORS = {
  booking_created: "#10b981", booking_cancelled: "#ef4444", booking_rescheduled: "#f59e0b",
  call_logged: "#3b82f6", lead_created: "#8b5cf6", usage_updated: "#64748b",
};

function EventRow({ event }) {
  const color = EVENT_COLORS[event.eventType] || "#64748b";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #1e293b" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 11, color, fontWeight: 600, width: 140, flexShrink: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {event.eventType?.replace(/_/g, " ")}
      </span>
      <span style={{ color: "#94a3b8", fontSize: 13, flex: 1 }}>{event.companySlug || ""}</span>
      <span style={{ color: "#475569", fontSize: 11 }}>{event.timestamp?.slice(0, 19)}</span>
    </div>
  );
}

/* ─── Home View ─────────────────────────────────────────── */

function HomeView({ companies, analytics, health, onSelectCompany }) {
  const totalCalls = Object.values(analytics).reduce((s, a) => s + (a.totalCalls || 0), 0);
  const totalBookings = Object.values(analytics).reduce((s, a) => s + (a.totalBookings || 0), 0);
  const totalLeads = Object.values(analytics).reduce((s, a) => s + (a.totalLeads || 0), 0);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
      <div style={{ marginBottom: 40 }}>
        <div style={{ color: "#10b981", fontSize: 11, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Control Center</div>
        <h1 data-testid="page-title" style={{ margin: 0, fontSize: 36, fontWeight: 800, color: "#f1f5f9" }}>Middleware Overview</h1>
        <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 15 }}>
          {health?.version && `v${health.version}`} &middot; {companies.length} connected {companies.length === 1 ? "client" : "clients"} &middot; Auto-refreshes every 30s
        </p>
      </div>

      <div data-testid="stats-row" style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        <StatCard label="Total Calls" value={totalCalls} />
        <StatCard label="Bookings" value={totalBookings} sub={totalCalls > 0 ? `${Math.round(totalBookings / totalCalls * 100)}% conversion` : ""} />
        <StatCard label="Leads" value={totalLeads} />
        <StatCard label="Clients" value={companies.length} sub="Active integrations" />
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: "#e2e8f0" }}>Connected Clients</h2>
      {companies.map((c) => {
        const a = analytics[c.id] || {};
        return (
          <div key={c.id} data-testid={`company-${c.slug}`} onClick={() => onSelectCompany(c)} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 24px", background: "#111827", border: "1px solid #1e293b",
            borderRadius: 10, marginBottom: 10, cursor: "pointer", transition: "border-color 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#10b981"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "#1e293b"}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#f1f5f9", fontSize: 15, fontWeight: 600 }}>{c.companyName}</div>
              <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{c.slug} &middot; {c.industry}</div>
            </div>
            <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
              {[
                { v: a.totalCalls ?? "...", l: "Calls", c: "#f1f5f9" },
                { v: a.totalBookings ?? "...", l: "Bookings", c: "#10b981" },
                { v: a.totalLeads ?? "...", l: "Leads", c: "#f59e0b" },
                { v: `${a.conversionRate ?? 0}%`, l: "Conv.", c: "#8b5cf6" },
              ].map(({ v, l, c: col }) => (
                <div key={l} style={{ textAlign: "center" }}>
                  <div style={{ color: col, fontSize: 18, fontWeight: 700 }}>{v}</div>
                  <div style={{ color: "#64748b", fontSize: 11 }}>{l}</div>
                </div>
              ))}
              <div style={{
                padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: c.dashboardMode === "api_push" ? "#1e1b4b" : "#052e16",
                color: c.dashboardMode === "api_push" ? "#a78bfa" : "#4ade80",
                border: `1px solid ${c.dashboardMode === "api_push" ? "#3730a3" : "#166534"}`,
              }}>{c.dashboardMode}</div>
              <span style={{ color: "#334155", fontSize: 18 }}>&rsaquo;</span>
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 60, paddingTop: 24, borderTop: "1px solid #1e293b", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#475569", fontSize: 12 }}>HiBotics AI Middleware &middot; {health?.version && `v${health.version}`}</div>
        <a data-testid="docs-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`} target="_blank" rel="noopener noreferrer"
          style={{ color: "#10b981", fontSize: 12, textDecoration: "none", padding: "6px 16px", borderRadius: 6, border: "1px solid #10b98133" }}>
          API Documentation
        </a>
      </div>
    </div>
  );
}

/* ─── Client Detail View ────────────────────────────────── */

function SectionTitle({ children }) {
  return <h3 style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #1e293b" }}>{children}</h3>;
}

function ClientDetailView({ company, analytics, headers }) {
  const [calls, setCalls] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [usage, setUsage] = useState([]);
  const [tab, setTab] = useState("overview");

  const fetchClientData = useCallback(async () => {
    const id = company.id;
    try {
      const [cRes, bRes, lRes, eRes, uRes] = await Promise.all([
        axios.get(`${API}/admin/calls/${id}?limit=50`, { headers }),
        axios.get(`${API}/admin/bookings/${id}?limit=50`, { headers }),
        axios.get(`${API}/admin/leads/${id}?limit=50`, { headers }),
        axios.get(`${API}/admin/events/${id}?limit=50`, { headers }),
        axios.get(`${API}/admin/usage/${id}`, { headers }),
      ]);
      setCalls(cRes.data?.data?.calls || []);
      setBookings(bRes.data?.data?.bookings || []);
      setLeads(lRes.data?.data?.leads || []);
      setEvents(eRes.data?.data?.events || []);
      setUsage(uRes.data?.data || []);
    } catch {}
  }, [company.id, headers]);

  useEffect(() => {
    fetchClientData();
    const interval = setInterval(fetchClientData, 30000);
    return () => clearInterval(interval);
  }, [fetchClientData]);

  const a = analytics || {};
  const TABS = [
    { key: "overview", label: "Overview" },
    { key: "calls", label: `Calls (${calls.length})` },
    { key: "bookings", label: `Bookings (${bookings.length})` },
    { key: "leads", label: `Leads (${leads.length})` },
    { key: "activity", label: "Activity" },
    { key: "api", label: "API Endpoints" },
    { key: "usage", label: "Billing" },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: "#10b981", fontSize: 11, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>{company.slug}</div>
        <h1 data-testid="client-title" style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#f1f5f9" }}>{company.companyName}</h1>
        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 14 }}>{company.industry} &middot; {company.contactName} &middot; {company.email}</p>
      </div>

      <div style={{ display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard label="Calls" value={a.totalCalls ?? 0} />
        <StatCard label="Bookings" value={a.totalBookings ?? 0} sub={a.conversionRate ? `${a.conversionRate}% conv.` : ""} />
        <StatCard label="Leads" value={a.totalLeads ?? 0} />
        <StatCard label="Avg Call" value={`${a.avgCallDurationSeconds ?? 0}s`} />
      </div>

      <div data-testid="tabs" style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #1e293b", paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} style={{
            background: tab === t.key ? "#111827" : "transparent",
            border: "1px solid", borderColor: tab === t.key ? "#1e293b" : "transparent",
            borderBottom: tab === t.key ? "1px solid #111827" : "1px solid transparent",
            color: tab === t.key ? "#f1f5f9" : "#64748b",
            padding: "10px 20px", borderRadius: "8px 8px 0 0", fontSize: 13, fontWeight: 600,
            cursor: "pointer", marginBottom: -1, transition: "all 0.15s",
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ background: "#111827", border: "1px solid #1e293b", borderRadius: "0 12px 12px 12px", padding: "24px", minHeight: 300 }}>
        {tab === "overview" && (
          <div>
            <SectionTitle>Call Outcomes</SectionTitle>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
              {Object.entries(a.callOutcomes || {}).map(([k, v]) => (
                <div key={k} style={{ background: "#0d1424", border: "1px solid #1e293b", borderRadius: 8, padding: "12px 20px" }}>
                  <div style={{ color: "#f1f5f9", fontSize: 20, fontWeight: 700 }}>{v}</div>
                  <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase" }}>{k.replace(/_/g, " ")}</div>
                </div>
              ))}
              {Object.keys(a.callOutcomes || {}).length === 0 && <span style={{ color: "#475569", fontSize: 13 }}>No call data yet</span>}
            </div>
            <SectionTitle>Booking Statuses</SectionTitle>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {Object.entries(a.bookingStatuses || {}).map(([k, v]) => (
                <div key={k} style={{ background: "#0d1424", border: "1px solid #1e293b", borderRadius: 8, padding: "12px 20px" }}>
                  <div style={{ color: k === "confirmed" ? "#10b981" : k === "cancelled" ? "#ef4444" : "#f59e0b", fontSize: 20, fontWeight: 700 }}>{v}</div>
                  <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase" }}>{k}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "calls" && (
          <div>
            <SectionTitle>Call Logs</SectionTitle>
            {calls.length === 0 ? <p style={{ color: "#475569" }}>No calls recorded yet</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid #1e293b" }}>
                  {["Caller", "Duration", "Outcome", "Summary", "Date"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{calls.map((c, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1e293b22" }}>
                    <td style={{ padding: "10px 12px", color: "#e2e8f0", fontSize: 13 }}>{c.callerNumber || c.callerName || "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>{c.durationSeconds}s</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 12,
                        background: c.outcome === "booking_created" ? "#052e16" : c.outcome === "lead_captured" ? "#1e1b4b" : "#1e293b",
                        color: c.outcome === "booking_created" ? "#4ade80" : c.outcome === "lead_captured" ? "#a78bfa" : "#94a3b8",
                      }}>{c.outcome}</span>
                    </td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.summary || "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#475569", fontSize: 11 }}>{c.createdAt?.slice(0, 16)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
        {tab === "bookings" && (
          <div>
            <SectionTitle>Bookings</SectionTitle>
            {bookings.length === 0 ? <p style={{ color: "#475569" }}>No bookings yet</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid #1e293b" }}>
                  {["Ref", "Customer", "Service", "Date/Time", "Status"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{bookings.map((b, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1e293b22" }}>
                    <td style={{ padding: "10px 12px", color: "#10b981", fontSize: 13, fontWeight: 600, fontFamily: "monospace" }}>{b.bookingReference}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ color: "#e2e8f0", fontSize: 13 }}>{b.customerName}</div>
                      <div style={{ color: "#475569", fontSize: 11 }}>{b.customerPhone}</div>
                    </td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12 }}>{b.serviceName}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12 }}>{b.start?.slice(0, 16)}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 12,
                        background: b.status === "confirmed" ? "#052e16" : b.status === "cancelled" ? "#450a0a" : "#1e293b",
                        color: b.status === "confirmed" ? "#4ade80" : b.status === "cancelled" ? "#fca5a5" : "#94a3b8",
                      }}>{b.status}</span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
        {tab === "leads" && (
          <div>
            <SectionTitle>Leads</SectionTitle>
            {leads.length === 0 ? <p style={{ color: "#475569" }}>No leads captured yet</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid #1e293b" }}>
                  {["Name", "Phone", "Email", "Service", "Status", "Date"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{leads.map((l, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1e293b22" }}>
                    <td style={{ padding: "10px 12px", color: "#e2e8f0", fontSize: 13 }}>{l.name}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>{l.phone}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12 }}>{l.email}</td>
                    <td style={{ padding: "10px 12px", color: "#f59e0b", fontSize: 12 }}>{l.serviceRequested}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 12, background: "#1e1b4b", color: "#a78bfa" }}>{l.status}</span>
                    </td>
                    <td style={{ padding: "10px 12px", color: "#475569", fontSize: 11 }}>{l.createdAt?.slice(0, 16)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
        {tab === "activity" && (
          <div>
            <SectionTitle>Recent Activity</SectionTitle>
            {events.length === 0 ? <p style={{ color: "#475569" }}>No recent events</p> : events.map((e, i) => <EventRow key={i} event={e} />)}
          </div>
        )}
        {tab === "api" && (
          <div>
            <SectionTitle>Middleware API Endpoints</SectionTitle>
            <p style={{ color: "#64748b", fontSize: 12, marginBottom: 16 }}>
              API Key: <code style={{ background: "#0d1424", padding: "2px 8px", borderRadius: 4, color: "#94a3b8", fontSize: 11 }}>Use X-API-Key header</code>
            </p>
            {[
              { section: "Booking Operations", endpoints: [
                { m: "POST", p: "/api/resources", d: "Get therapists/staff" },
                { m: "POST", p: "/api/services", d: "Get available services" },
                { m: "POST", p: "/api/availability", d: "Check available time slots" },
                { m: "POST", p: "/api/bookings", d: "Create a booking" },
                { m: "POST", p: "/api/bookings/search", d: "Search bookings" },
                { m: "POST", p: "/api/bookings/reschedule", d: "Reschedule booking" },
                { m: "POST", p: "/api/bookings/cancel", d: "Cancel booking" },
              ]},
              { section: "Leads & Calls", endpoints: [
                { m: "POST", p: "/api/leads", d: "Create a lead" },
                { m: "POST", p: "/api/calls/log", d: "Log call" },
                { m: "POST", p: "/api/usage/sync", d: "Sync usage" },
              ]},
              { section: "Webhooks", endpoints: [
                { m: "POST", p: "/api/webhooks/retell", d: "Unified Retell webhook" },
              ]},
              { section: "Reminders", endpoints: [
                { m: "GET", p: `/api/admin/reminders/status/{ref}`, d: "Reminder status" },
                { m: "GET", p: `/api/admin/reminders/batch?company_id=${company.id}`, d: "Batch reminder status" },
              ]},
              { section: "Dashboard Data", endpoints: [
                { m: "GET", p: `/api/admin/analytics/${company.id}`, d: "Analytics" },
                { m: "GET", p: `/api/admin/calls/${company.id}`, d: "Calls" },
                { m: "GET", p: `/api/admin/bookings/${company.id}`, d: "Bookings" },
                { m: "GET", p: `/api/admin/leads/${company.id}`, d: "Leads" },
                { m: "GET", p: `/api/admin/events/${company.id}`, d: "Events" },
              ]},
            ].map(section => (
              <div key={section.section} style={{ marginBottom: 20 }}>
                <div style={{ color: "#64748b", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>{section.section}</div>
                {section.endpoints.map((ep, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0", borderBottom: "1px solid #1e293b22" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                      background: ep.m === "GET" ? "#0c4a6e" : "#052e16", color: ep.m === "GET" ? "#7dd3fc" : "#4ade80",
                      fontFamily: "monospace", width: 42, textAlign: "center",
                    }}>{ep.m}</span>
                    <code style={{ fontSize: 11, color: "#94a3b8", flex: 1, fontFamily: "'JetBrains Mono', monospace" }}>{ep.p}</code>
                    <span style={{ fontSize: 11, color: "#475569" }}>{ep.d}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {tab === "usage" && (
          <div>
            <SectionTitle>Billing &amp; Usage</SectionTitle>
            {usage.length === 0 ? <p style={{ color: "#475569" }}>No usage data</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid #1e293b" }}>
                  {["Month", "Minutes", "Rate", "Usage", "Retainer", "Total", "Status"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{usage.map((u, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1e293b22" }}>
                    <td style={{ padding: "10px 12px", color: "#e2e8f0", fontSize: 13 }}>{u.month}/{u.year}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>{u.minutesUsed}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>{u.minuteRate}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>{u.usageCost}</td>
                    <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>{u.monthlyRetainer}</td>
                    <td style={{ padding: "10px 12px", color: "#f1f5f9", fontSize: 13, fontWeight: 600 }}>{u.totalCost}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 12,
                        background: u.paymentStatus === "paid" ? "#052e16" : "#451a03",
                        color: u.paymentStatus === "paid" ? "#4ade80" : "#fbbf24",
                      }}>{u.paymentStatus}</span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── App ───────────────────────────────────────────────── */

export default function App() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("hb_admin_key") || "");
  const [health, setHealth] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const headers = adminKey ? { "X-Admin-Key": adminKey } : {};

  const handleLogin = (key, token) => {
    setAdminKey(key);
    sessionStorage.setItem("hb_admin_key", key);
    sessionStorage.setItem("hb_token", token);
  };

  const handleLogout = () => {
    setAdminKey("");
    sessionStorage.removeItem("hb_admin_key");
    sessionStorage.removeItem("hb_token");
    setCompanies([]);
    setAnalytics({});
    setSelectedCompany(null);
  };

  const fetchData = useCallback(async () => {
    if (!adminKey) { setLoading(false); return; }
    try {
      const [healthRes, companiesRes] = await Promise.all([
        axios.get(`${API}/admin/health`),
        axios.get(`${API}/admin/companies`, { headers: { "X-Admin-Key": adminKey } }),
      ]);
      setHealth(healthRes.data);
      const comps = companiesRes.data?.data || [];
      setCompanies(comps);

      const analyticsMap = {};
      await Promise.all(comps.map(async (c) => {
        try {
          const aRes = await axios.get(`${API}/admin/analytics/${c.id}`, { headers: { "X-Admin-Key": adminKey } });
          analyticsMap[c.id] = aRes.data?.data || {};
        } catch {}
      }));
      setAnalytics(analyticsMap);
    } catch (err) {
      if (err.response?.status === 403) { handleLogout(); return; }
      setHealth({ status: "error" });
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (!adminKey) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div data-testid="middleware-dashboard" style={{
      minHeight: "100vh", background: "#070b14", color: "#f1f5f9",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <Nav health={health} loading={loading} view={selectedCompany ? "detail" : "home"}
        onBack={() => setSelectedCompany(null)} onLogout={handleLogout} />
      {selectedCompany ? (
        <ClientDetailView company={selectedCompany} analytics={analytics[selectedCompany.id]} headers={headers} />
      ) : (
        <HomeView companies={companies} analytics={analytics} health={health} onSelectCompany={setSelectedCompany} />
      )}
    </div>
  );
}
